/**
 * RewardTemplateForm — admin reward authoring (Plan 02-12 Task 2 / D-11
 * Layer 1 visible UX / REW-01).
 *
 * Fields:
 *   - title           required, ≤ 120 chars
 *   - description     optional, ≤ 280 chars
 *   - category        required, REWARD_CATEGORIES enum chip-row
 *   - point_cost      required, ≥ 1
 *   - icon_key        optional
 *
 * Validation:
 *   The server (Plan 02-05 createReward) is authoritative — it runs the
 *   Layer 1 keyword filter against every household member's allergies and
 *   returns 422 'reward_authoring_blocked' on collision. The form maps
 *   that to an inline error referencing the matched_keyword from the
 *   response details.
 *
 * Norwegian copy:
 *   "Denne belønningen kan kollidere med familiens {{keyword}}-allergi
 *    — endre tittel eller velg annen kategori."
 *
 * Permission gating:
 *   The route mounting this form (ApprovalsRoute, under the Bytter filter
 *   since #155) gates on role==='admin'. The server's POST /api/rewards endpoint also chains
 *   requireAdminScope (D-15), so this form can't be exploited by a
 *   non-admin even if the UI is somehow rendered.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { REWARD_CATEGORIES } from '@stell/shared';
import { useCreateReward } from './useRewardsQueries';
import type { RewardCategory } from './useRewardsQueries';

interface RewardTemplateFormProps {
  /** Fired with the created reward when the server accepts it. */
  onCreated?: (reward: { id: string; title: string }) => void;
}

export function RewardTemplateForm({
  onCreated,
}: RewardTemplateFormProps): ReactNode {
  const { t } = useTranslation();
  const create = useCreateReward();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<RewardCategory>('annet');
  const [cost, setCost] = useState('1');
  const [iconKey, setIconKey] = useState('');
  const [errorBody, setErrorBody] = useState<string | null>(null);

  const handleSubmit = (ev: FormEvent): void => {
    ev.preventDefault();
    setErrorBody(null);
    const numericCost = Number.parseInt(cost, 10);
    if (Number.isNaN(numericCost) || numericCost < 1) {
      setErrorBody(t('rewards.admin.error.generic'));
      return;
    }
    create.mutate(
      {
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        category,
        point_cost: numericCost,
        ...(iconKey.trim() ? { icon_key: iconKey.trim() } : {}),
      },
      {
        onSuccess: (row) => {
          // Reset the form to the initial state on success.
          setTitle('');
          setDescription('');
          setCategory('annet');
          setCost('1');
          setIconKey('');
          onCreated?.(row);
        },
        onError: (err) => {
          const body = err.body as
            | { error?: string; matched_keyword?: string }
            | undefined;
          if (body?.error === 'reward_authoring_blocked' && body.matched_keyword) {
            setErrorBody(
              t('rewards.admin.error.allergyClash', {
                keyword: body.matched_keyword,
              }),
            );
          } else {
            setErrorBody(t('rewards.admin.error.generic'));
          }
        },
      },
    );
  };

  return (
    <form
      data-testid="reward-template-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
    >
      <h2 className="font-display text-display-s text-ink m-0">
        {t('rewards.admin.heading')}
      </h2>

      {/* Title */}
      <label className="flex flex-col gap-1 text-sm font-medium">
        <span>{t('rewards.admin.title')}</span>
        <input
          data-testid="reward-form-title"
          type="text"
          required
          maxLength={120}
          value={title}
          onChange={(ev) => setTitle(ev.target.value)}
          className="rounded-md border border-card-line bg-sheet px-3 py-2 text-sm"
        />
      </label>

      {/* Description */}
      <label className="flex flex-col gap-1 text-sm font-medium">
        <span>{t('rewards.admin.description')}</span>
        <textarea
          data-testid="reward-form-description"
          maxLength={280}
          rows={2}
          value={description}
          onChange={(ev) => setDescription(ev.target.value)}
          className="rounded-md border border-card-line bg-sheet px-3 py-2 text-sm"
        />
      </label>

      {/* Category chip-row */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          {t('rewards.admin.category')}
        </legend>
        <div className="flex flex-wrap gap-2">
          {REWARD_CATEGORIES.map((c) => {
            const selected = c === category;
            return (
              <button
                key={c}
                type="button"
                data-testid="reward-form-category-chip"
                data-category={c}
                aria-pressed={selected}
                onClick={() => setCategory(c)}
                className={[
                  'inline-flex items-center rounded-pill px-3 py-1.5 text-sm font-medium',
                  selected
                    ? 'bg-ink text-sheet'
                    : 'bg-raised text-ink border border-card-line',
                ].join(' ')}
              >
                {t(`rewards.admin.categoryLabel.${c}`)}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Cost */}
      <label className="flex flex-col gap-1 text-sm font-medium">
        <span>{t('rewards.admin.cost')}</span>
        <input
          data-testid="reward-form-cost"
          type="number"
          min={1}
          required
          value={cost}
          onChange={(ev) => setCost(ev.target.value)}
          className="rounded-md border border-card-line bg-sheet px-3 py-2 text-sm font-mono tnum"
        />
      </label>

      {/* Icon key (optional) */}
      <label className="flex flex-col gap-1 text-sm font-medium">
        <span>{t('rewards.admin.iconKey')}</span>
        <input
          data-testid="reward-form-icon"
          type="text"
          value={iconKey}
          onChange={(ev) => setIconKey(ev.target.value)}
          className="rounded-md border border-card-line bg-sheet px-3 py-2 text-sm"
        />
      </label>

      {errorBody ? (
        <p
          data-testid="reward-form-error"
          className="rounded-md border border-bad bg-raised px-3 py-2 text-sm text-ink"
        >
          {errorBody}
        </p>
      ) : null}

      <button
        type="submit"
        data-testid="reward-form-submit"
        disabled={create.isPending}
        className="inline-flex items-center justify-center rounded-pill bg-primary text-on-primary px-4 py-2 text-sm font-semibold disabled:opacity-50"
      >
        {create.isPending ? (
          <span
            data-testid="reward-form-submit-spinner"
            aria-hidden
            className="inline-block h-3 w-3 animate-pulse rounded-full bg-sheet"
          />
        ) : (
          t('rewards.admin.submit')
        )}
      </button>
    </form>
  );
}
